import { supabase } from '../lib/supabase';
import { questions } from '../data/questions';
import { DOMAIN_QUESTION_COUNT, VALUES } from '../data/values';
import { Option } from '../types';

export async function runDiagnostics() {
  const results = {
    supabaseConnection: false,
    databaseAccess: false,
    emailFunctionality: false,
    errors: [] as string[],
    questionValidation: validateQuestions()
  };

  try {
    // Test 1: Supabase Connection
    const { data: connectionTest, error: connectionError } = await supabase
      .from('users')
      .select('id')
      .limit(1);

    results.supabaseConnection = !connectionError;
    if (connectionError) {
      results.errors.push(`Supabase connection error: ${connectionError.message}`);
    }

    // Test 2: Database Access
    const { data: dbTest, error: dbError } = await supabase
      .from('test_results')
      .select('id')
      .limit(1);

    results.databaseAccess = !dbError;
    if (dbError) {
      results.errors.push(`Database access error: ${dbError.message}`);
    }

    // Test 3: Email Functionality
    const { data: emailTest, error: emailError } = await supabase.functions.invoke('test-email', {
      body: { test: true }
    });

    results.emailFunctionality = !emailError;
    if (emailError) {
      results.errors.push(`Email functionality error: ${emailError.message}`);
    }

    return results;
  } catch (error) {
    results.errors.push(`Unexpected error: ${error instanceof Error ? error.message : String(error)}`);
    return results;
  }
}

export function validateQuestions() {
  const results = {
    isValid: true,
    errors: [] as string[],
    stats: {
      totalQuestions: questions.length,
      questionsByDomain: {} as Record<string, number>,
      scoreRanges: {
        min: Infinity,
        max: -Infinity
      }
    }
  };

  // Count questions per domain
  questions.forEach(q => {
    results.stats.questionsByDomain[q.category] = 
      (results.stats.questionsByDomain[q.category] || 0) + 1;
  });

  // Validate domain counts
  VALUES.forEach(domain => {
    const expected = DOMAIN_QUESTION_COUNT[domain];
    const actual = results.stats.questionsByDomain[domain] || 0;
    if (actual !== expected) {
      results.isValid = false;
      results.errors.push(
        `Domain ${domain} has ${actual} questions but should have ${expected}`
      );
    }
  });

  // Validate options and scoring
  questions.forEach((q, idx) => {
    // Check number of options
    if (q.options.length !== 7) {
      results.isValid = false;
      results.errors.push(
        `Question ${idx + 1} has ${q.options.length} options instead of 7`
      );
    }

    // Check scoring ranges
    q.options.forEach((opt: Option) => {
      Object.entries(opt.scores).forEach(([archetype, score]) => {
        results.stats.scoreRanges.min = Math.min(
          results.stats.scoreRanges.min, 
          score
        );
        results.stats.scoreRanges.max = Math.max(
          results.stats.scoreRanges.max, 
          score
        );

        if (score < 1 || score > 5) {
          results.isValid = false;
          results.errors.push(
            `Invalid score ${score} for archetype ${archetype} in question ${idx + 1}`
          );
        }
      });
    });
  });

  return results;
}